import { useState, useMemo, useCallback, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMathEducation } from './MathEducationProvider';
import { classifyRiceWater, hasPoint, isAllPerfectFound, PERFECT_PAIRS, type RiceResult } from './riceGameLogic';
import { Panel } from './ui/panel';
import { Button } from './ui/button';

const GRID_MIN = 1;
const GRID_MAX = 10;
const GRID_PADDING = 50;
const GRID_SIZE = 400;
const CELL_SIZE = GRID_SIZE / GRID_MAX;

const EMOJI: Record<RiceResult, string> = {
  perfect: '🍚',
  soggy: '💧',
  crunchy: '🔥',
};

const LABEL: Record<RiceResult, string> = {
  perfect: 'Perfect!',
  soggy: 'Too Soggy!',
  crunchy: 'Too Crunchy!',
};

function gridX(rice: number): number {
  return GRID_PADDING + rice * CELL_SIZE;
}
function gridY(water: number): number {
  return GRID_PADDING + GRID_SIZE - water * CELL_SIZE;
}

interface SparkleState {
  key: number;
  cx: number;
  cy: number;
}

export function RiceCookingGame() {
  const navigate = useNavigate();
  const { progress, updateProgress } = useMathEducation();
  const points = progress.exceedRiceGamePoints ?? [];
  const complete = useMemo(() => isAllPerfectFound(points), [points]);

  const [rice, setRice] = useState(1);
  const [water, setWater] = useState(1);
  const [sparkles, setSparkles] = useState<SparkleState[]>([]);

  const alreadySubmitted = useMemo(() => hasPoint(points, rice, water), [points, rice, water]);

  const handleTryIt = useCallback(() => {
    if (alreadySubmitted) return;

    const result = classifyRiceWater(rice, water);
    const newPoints = [...points, { rice, water }];
    updateProgress({ exceedRiceGamePoints: newPoints });

    if (result === 'perfect') {
      setSparkles(prev => [...prev, { key: Date.now(), cx: gridX(rice), cy: gridY(water) }]);
    }
  }, [alreadySubmitted, rice, water, points, updateProgress]);

  const handleSparkleEnd = useCallback((key: number) => {
    setSparkles(prev => prev.filter(s => s.key !== key));
  }, []);

  const svgId = useId();

  const totalWidth = GRID_SIZE + GRID_PADDING * 2;
  const totalHeight = GRID_SIZE + GRID_PADDING * 2;

  return (
    <Panel className="tw-flex-1">
      <div
        className="tw-relative tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-items-center tw-gap-4 tw-overflow-auto"
        style={{ padding: 'var(--spacing-4, 16px)' }}
      >
        {/* Instructions + COMPLETE stamp wrapper */}
        <div className="tw-relative tw-text-center">
          <h2 className="tw-m-0 tw-font-atkinson tw-text-lg tw-font-bold" style={{ color: 'var(--foreground)' }}>
            Find the perfect rice recipe! Can you make smaller and larger batches?
          </h2>
          <p className="tw-m-0 tw-mt-2 tw-font-atkinson tw-text-lg" style={{ color: 'var(--muted-foreground)' }}>
            {PERFECT_PAIRS.filter(([r, w]) => hasPoint(points, r, w)).length} / {PERFECT_PAIRS.length} perfect recipes
            found
          </p>

          {complete && (
            <div
              className="tw-absolute tw-pointer-events-none"
              style={{ zIndex: 20, right: '-24px', top: '50%', transform: 'translateY(-20%)' }}
            >
              <div
                style={{
                  border: '4px solid #16a34a',
                  padding: '8px 32px',
                  transform: 'rotate(-6deg)',
                  backgroundColor: 'rgba(255,255,255,0.92)',
                }}
              >
                <span
                  className="tw-font-atkinson tw-font-bold"
                  style={{ fontSize: '32px', color: '#16a34a', letterSpacing: '4px' }}
                >
                  COMPLETE!
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Grid + Controls */}
        <div className="tw-flex tw-items-start tw-gap-6 tw-flex-wrap tw-justify-center">
          {/* SVG Grid */}
          <svg
            viewBox={`0 0 ${totalWidth} ${totalHeight}`}
            width={totalWidth}
            height={totalHeight}
            className="tw-shrink-0"
            style={{ maxWidth: '100%', height: 'auto' }}
          >
            {/* Gridlines */}
            {Array.from({ length: GRID_MAX }, (_, i) => i + 1).map(v => (
              <g key={`grid-${v}`}>
                <line
                  x1={GRID_PADDING}
                  y1={GRID_PADDING + GRID_SIZE - v * CELL_SIZE}
                  x2={GRID_PADDING + GRID_SIZE}
                  y2={GRID_PADDING + GRID_SIZE - v * CELL_SIZE}
                  stroke="var(--border)"
                  strokeWidth={0.5}
                />
                <line
                  x1={GRID_PADDING + v * CELL_SIZE}
                  y1={GRID_PADDING}
                  x2={GRID_PADDING + v * CELL_SIZE}
                  y2={GRID_PADDING + GRID_SIZE}
                  stroke="var(--border)"
                  strokeWidth={0.5}
                />
              </g>
            ))}

            {/* Axes */}
            <line
              x1={GRID_PADDING}
              y1={GRID_PADDING + GRID_SIZE}
              x2={GRID_PADDING + GRID_SIZE}
              y2={GRID_PADDING + GRID_SIZE}
              stroke="var(--foreground)"
              strokeWidth={1.5}
            />
            <line
              x1={GRID_PADDING}
              y1={GRID_PADDING}
              x2={GRID_PADDING}
              y2={GRID_PADDING + GRID_SIZE}
              stroke="var(--foreground)"
              strokeWidth={1.5}
            />

            {/* Tick labels */}
            {Array.from({ length: GRID_MAX }, (_, i) => i + 1).map(v => (
              <g key={`tick-${v}`}>
                {/* X-axis tick */}
                <text
                  x={GRID_PADDING + v * CELL_SIZE}
                  y={GRID_PADDING + GRID_SIZE + 18}
                  textAnchor="middle"
                  fontSize="12"
                  fill="var(--foreground)"
                  fontFamily="var(--font-atkinson)"
                >
                  {v}
                </text>
                {/* Y-axis tick */}
                <text
                  x={GRID_PADDING - 10}
                  y={GRID_PADDING + GRID_SIZE - v * CELL_SIZE + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="var(--foreground)"
                  fontFamily="var(--font-atkinson)"
                >
                  {v}
                </text>
              </g>
            ))}

            {/* Axis labels */}
            <text
              x={GRID_PADDING + GRID_SIZE / 2}
              y={GRID_PADDING + GRID_SIZE + 40}
              textAnchor="middle"
              fontSize="14"
              fontWeight="bold"
              fill="var(--foreground)"
              fontFamily="var(--font-atkinson)"
            >
              Cups of Rice
            </text>
            <text
              x={14}
              y={GRID_PADDING + GRID_SIZE / 2}
              textAnchor="middle"
              fontSize="14"
              fontWeight="bold"
              fill="var(--foreground)"
              fontFamily="var(--font-atkinson)"
              transform={`rotate(-90, 14, ${GRID_PADDING + GRID_SIZE / 2})`}
            >
              Cups of Water
            </text>

            {/* Plotted points */}
            {points.map((p, i) => {
              const result = classifyRiceWater(p.rice, p.water);
              const cx = gridX(p.rice);
              const cy = gridY(p.water);
              const isLatest = i === points.length - 1;
              return (
                <g key={`${p.rice}-${p.water}-${i}`}>
                  <text x={cx} y={cy + 6} textAnchor="middle" fontSize="20" style={{ pointerEvents: 'none' }}>
                    {EMOJI[result]}
                  </text>
                  {(result === 'perfect' || isLatest) && (
                    <text
                      x={cx}
                      y={cy - 14}
                      textAnchor="middle"
                      fontSize="9"
                      fontWeight="bold"
                      fontFamily="var(--font-atkinson)"
                      fill={result === 'perfect' ? '#16a34a' : result === 'soggy' ? '#2563eb' : '#dc2626'}
                    >
                      {LABEL[result]}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Current position highlight */}
            <circle
              cx={gridX(rice)}
              cy={gridY(water)}
              r={16}
              fill="none"
              stroke="#7c3aed"
              strokeWidth={2}
              strokeDasharray="4 2"
              opacity={alreadySubmitted ? 0.3 : 0.8}
            >
              <animate attributeName="r" values="14;17;14" dur="1.5s" repeatCount="indefinite" />
            </circle>

            {/* Sparkle animations */}
            {sparkles.map(s => (
              <SparkleGroup key={s.key} cx={s.cx} cy={s.cy} svgId={svgId} onEnd={() => handleSparkleEnd(s.key)} />
            ))}
          </svg>
          {complete && (
            <p className="tw-m-0 tw-text-sm tw-font-atkinson" style={{ color: 'var(--muted-foreground)' }}>
              The perfect ratio is 1 cup of rice to 2 cups of water.
            </p>
          )}

          {/* Controls */}
          <div className="tw-flex tw-flex-col tw-gap-4 tw-min-w-[200px]" style={{ paddingTop: '16px' }}>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <label className="tw-font-atkinson tw-font-bold tw-text-sm" style={{ color: 'var(--foreground)' }}>
                Cups of Rice: {rice}
              </label>
              <input
                type="range"
                min={GRID_MIN}
                max={GRID_MAX}
                value={rice}
                onChange={e => setRice(Number(e.target.value))}
                className="tw-w-full tw-accent-purple-600"
              />
              <div className="tw-flex tw-justify-between tw-text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <span>{GRID_MIN}</span>
                <span>{GRID_MAX}</span>
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-1">
              <label className="tw-font-atkinson tw-font-bold tw-text-sm" style={{ color: 'var(--foreground)' }}>
                Cups of Water: {water}
              </label>
              <input
                type="range"
                min={GRID_MIN}
                max={GRID_MAX}
                value={water}
                onChange={e => setWater(Number(e.target.value))}
                className="tw-w-full tw-accent-blue-500"
              />
              <div className="tw-flex tw-justify-between tw-text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <span>{GRID_MIN}</span>
                <span>{GRID_MAX}</span>
              </div>
            </div>

            <div className="tw-text-center tw-text-sm tw-font-atkinson" style={{ color: 'var(--muted-foreground)' }}>
              ({rice} rice, {water} water)
            </div>

            <Button onClick={handleTryIt} disabled={alreadySubmitted} className="tw-font-atkinson" size="lg">
              {alreadySubmitted ? 'Already Tried!' : 'Try It!'}
            </Button>

            {complete && (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="tw-font-atkinson tw-mt-2 tw-cursor-pointer"
                  style={{
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    padding: '10px 24px',
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-weight-bold)',
                    boxShadow: '0 0 20px rgba(22,163,74,0.5), 0 0 40px rgba(21,128,61,0.3)',
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s',
                    animation: 'nextSkillPulse 1.5s ease-in-out infinite',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.15)';
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(22,163,74,0.6), 0 0 60px rgba(21,128,61,0.4)';
                    e.currentTarget.style.animation = 'none';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(22,163,74,0.5), 0 0 40px rgba(21,128,61,0.3)';
                    e.currentTarget.style.animation = 'nextSkillPulse 1.5s ease-in-out infinite';
                  }}
                >
                  <span style={{ position: 'relative', zIndex: 2 }}>Choose Your Next Skill &rarr;</span>
                  <div
                    style={{
                      position: 'absolute',
                      top: '-50%',
                      left: '-50%',
                      width: '200%',
                      height: '200%',
                      background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
                      animation: 'nextSkillShimmer 2.5s infinite',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                </button>
                <style>{`
                  @keyframes nextSkillPulse {
                    0%, 100% {
                      transform: scale(1);
                      box-shadow: 0 0 20px rgba(22,163,74,0.5), 0 0 40px rgba(21,128,61,0.3);
                    }
                    50% {
                      transform: scale(1.12);
                      box-shadow: 0 0 30px rgba(22,163,74,0.6), 0 0 60px rgba(21,128,61,0.4);
                    }
                  }
                  @keyframes nextSkillShimmer {
                    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
                    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
                  }
                `}</style>
              </>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

// Sparkle burst + "Perfect!" float animation rendered in SVG
const SPARKLE_DURATION_MS = 800;

function SparkleGroup({ cx, cy, svgId, onEnd }: { cx: number; cy: number; svgId: string; onEnd: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onEnd, SPARKLE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onEnd]);

  const particles = useMemo(() => {
    const count = 8;
    return Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2;
      const dist = 22 + Math.random() * 12;
      return { dx: Math.cos(angle) * dist, dy: Math.sin(angle) * dist };
    });
  }, []);

  return (
    <g>
      {/* "Perfect!" text floats upward */}
      <text
        x={cx}
        y={cy - 20}
        textAnchor="middle"
        fontSize="13"
        fontWeight="bold"
        fontFamily="var(--font-atkinson)"
        fill="#16a34a"
      >
        Perfect!
        <animate attributeName="y" from={cy - 20} to={cy - 50} dur="0.8s" fill="freeze" />
        <animate attributeName="opacity" from="1" to="0" dur="0.8s" fill="freeze" />
      </text>

      {/* Star particles */}
      {particles.map((p, i) => (
        <text
          key={`${svgId}-sparkle-${i}`}
          x={cx}
          y={cy}
          textAnchor="middle"
          fontSize="10"
          style={{ pointerEvents: 'none' }}
        >
          ✦
          <animate attributeName="x" from={cx} to={cx + p.dx} dur="0.6s" fill="freeze" />
          <animate attributeName="y" from={cy} to={cy + p.dy} dur="0.6s" fill="freeze" />
          <animate attributeName="opacity" from="1" to="0" dur="0.6s" fill="freeze" />
        </text>
      ))}
    </g>
  );
}
